import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// 30-second default timeout — without this, a hung backend keeps requests
// pending forever and the UI feels permanently frozen (one of the user's
// top complaints). Long-running endpoints like PDF generation override this
// with their own per-request timeout below.
const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors + surface timeouts so the UI never silently hangs.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // FastAPI 422 returns detail as a list of objects — turn it into readable text so toasts never crash the page
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      const field = (d) => (Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join(' ').replace(/_/g, ' ') : '');
      error.response.data.detail = detail
        .map((d) => (typeof d === 'string' ? d : [field(d), (d?.msg || 'invalid').replace(/^Value error, /, '')].filter(Boolean).join(': ')))
        .join('; ');
    }
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Use replace so the back button doesn't bounce us back into a dead session
      window.location.replace('/login');
      return Promise.reject(error);
    }
    // Callers can opt out of the global timeout/offline toast by setting
    // `config.silentError = true` — used by polling / retryable requests
    // (e.g. bank-statement job status) where the caller handles its own UX.
    const silent = error?.config?.silentError === true;
    if (!silent) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out — please check your connection and try again.');
      } else if (!error.response && error.message === 'Network Error') {
        toast.error('Lost connection to the server. Reconnecting…');
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  register: (data) => api.post('/auth/register', data, { silentError: true }),
  login: (data) => api.post('/auth/login', data, { silentError: true }),
  getMe: () => api.get('/auth/me', { silentError: true }),
};

// Units
export const unitsApi = {
  getAll: () => api.get('/units'),
  getById: (id) => api.get(`/units/${id}`),
  create: (data) => api.post('/units', data),
  update: (id, data) => api.put(`/units/${id}`, data),
  setStatus: (id, status, inactiveSince = null) =>
    api.patch(`/units/${id}/status`, { status, inactive_since: inactiveSince }),
  delete: (id) => api.delete(`/units/${id}`),
};

// Drivers
export const driversApi = {
  getAll: () => api.get('/drivers'),
  getById: (id) => api.get(`/drivers/${id}`),
  create: (data) => api.post('/drivers', data),
  update: (id, data) => api.put(`/drivers/${id}`, data),
  setStatus: (id, status) => api.patch(`/drivers/${id}/status`, { status }),
  getPaySummaries: (weekStart) => api.get('/drivers/pay-summaries', { params: { week_start: weekStart } }),
  deductFromUnit: (id, weekStart) => api.post(`/drivers/${id}/deduct-from-unit`, { week_start: weekStart }),
  undoDeductFromUnit: (id, weekStart) => api.delete(`/drivers/${id}/deduct-from-unit`, { params: { week_start: weekStart } }),
  delete: (id) => api.delete(`/drivers/${id}`),
  getPaystub: (id, weekStart, endDate = null) => {
    const params = { week_start: weekStart };
    if (endDate) params.end_date = endDate;
    return api.get(`/drivers/${id}/paystub`, { params, responseType: 'blob' });
  },
  getPaySummary: (id, weekStart, endDate = null) => {
    const params = { week_start: weekStart };
    if (endDate) params.end_date = endDate;
    return api.get(`/drivers/${id}/pay-summary`, { params });
  },
  togglePaystubLoad: (driverId, loadId, excluded) =>
    api.post(`/drivers/${driverId}/paystub-load-toggle`, { load_id: loadId, excluded }),
};

// Driver Deductions
export const driverDeductionsApi = {
  getAll: (driverId = null, weekStart = null) => {
    const params = {};
    if (driverId) params.driver_id = driverId;
    if (weekStart) params.week_start = weekStart;
    return api.get('/driver-deductions', { params });
  },
  create: (data) => api.post('/driver-deductions', data),
  delete: (id) => api.delete(`/driver-deductions/${id}`),
};

// Driver Paystub Addons (Bonuses, etc.)
export const driverAddonsApi = {
  getAll: (driverId = null, weekStart = null) => {
    const params = {};
    if (driverId) params.driver_id = driverId;
    if (weekStart) params.week_start = weekStart;
    return api.get('/driver-addons', { params });
  },
  create: (data) => api.post('/driver-addons', data),
  delete: (id) => api.delete(`/driver-addons/${id}`),
};

// Staff (dispatchers, contractors, owner operators, custom roles)
export const staffApi = {
  getAll: (filters = {}) => api.get('/staff', { params: filters }),
  getById: (id) => api.get(`/staff/${id}`),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`),
  getPaySummary: (id, weekStart, opts = {}) => {
    const params = { week_start: weekStart, ...opts };
    return api.get(`/staff/${id}/pay-summary`, { params });
  },
  getPaystub: (id, weekStart, opts = {}) => {
    const params = { week_start: weekStart, ...opts };
    return api.get(`/staff/${id}/paystub`, { params, responseType: 'blob' });
  },
  // Addons
  listAddons: (id, weekStart = null) => {
    const params = {};
    if (weekStart) params.week_start = weekStart;
    return api.get(`/staff/${id}/addons`, { params });
  },
  addAddon: (id, data) => api.post(`/staff/${id}/addons`, data),
  deleteAddon: (addonId) => api.delete(`/staff/addons/${addonId}`),
  // Deductions
  listDeductions: (id, weekStart = null) => {
    const params = {};
    if (weekStart) params.week_start = weekStart;
    return api.get(`/staff/${id}/deductions`, { params });
  },
  addDeduction: (id, data) => api.post(`/staff/${id}/deductions`, data),
  deleteDeduction: (dedId) => api.delete(`/staff/deductions/${dedId}`),
};

// Company expenses (office, parking, etc.)
export const expensesApi = {
  getAll: (filters = {}) => api.get('/expenses', { params: filters }),
  getCategories: () => api.get('/expenses/categories'),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  bulkDelete: (ids) => api.post('/expenses/bulk-delete', { expense_ids: ids }),
  getSummary: (startDate, endDate) =>
    api.get('/expenses/summary', { params: { start_date: startDate, end_date: endDate } }),
};

// Owner Operators
export const preferencesApi = {
  getSummaryUnitOrder: () => api.get('/me/summary-unit-order'),
  saveSummaryUnitOrder: (unitIds) => api.put('/me/summary-unit-order', { unit_ids: unitIds }),
};

export const ownerOperatorsApi = {
  getAll: () => api.get('/owner-operators'),
  getById: (id) => api.get(`/owner-operators/${id}`),
  create: (data) => api.post('/owner-operators', data),
  update: (id, data) => api.put(`/owner-operators/${id}`, data),
  delete: (id) => api.delete(`/owner-operators/${id}`),
  setStatus: (id, status) => api.patch(`/owner-operators/${id}/status`, { status }),
  getSummary: (id, weekStart, endDate = null) => {
    const params = { week_start: weekStart };
    if (endDate) params.end_date = endDate;
    return api.get(`/owner-operators/${id}/summary`, { params });
  },
  exportPdf: (id, weekStart, endDate = null, itemized = false) => {
    const params = { week_start: weekStart, itemized };
    if (endDate) params.end_date = endDate;
    return api.get(`/owner-operators/${id}/export-pdf`, { params, responseType: 'blob' });
  },
};

// Loads
export const loadsApi = {
  getAll: (params) => api.get('/loads', { params }),
  getById: (id) => api.get(`/loads/${id}`),
  create: (data, config) => api.post('/loads', data, config),
  update: (id, data) => api.put(`/loads/${id}`, data),
  delete: (id) => api.delete(`/loads/${id}`),

  // Broker document import (Rate Con / BOL) — async job flow
  parseDocuments: (files, weekStart) => {
    const fd = new FormData();
    (Array.isArray(files) ? files : [files]).forEach((f) => fd.append('files', f));
    fd.append('week_start', weekStart);
    return api.post('/loads/import-documents/parse', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  parseStatus: (jobId) => api.get(`/loads/import-documents/status/${jobId}`),
  commitDocuments: ({ weekStart, parsedLoads, overrides = {}, forceCrossWeek = false, skipRefs = [], forceRefs = [] }) => {
    const fd = new FormData();
    fd.append('week_start', weekStart);
    fd.append('dry_run', 'false');
    fd.append('parsed_loads_json', JSON.stringify(parsedLoads));
    fd.append('overrides', JSON.stringify(overrides));
    fd.append('force_cross_week', forceCrossWeek ? 'true' : 'false');
    fd.append('skip_load_refs', JSON.stringify(skipRefs));
    fd.append('force_refs', JSON.stringify(forceRefs));
    // FastAPI's `files: List[UploadFile]=File(...)` requires at least one file
    // even at commit time. Send a 1-byte placeholder so the endpoint doesn't 422.
    fd.append('files', new Blob(['_'], { type: 'application/pdf' }), '_placeholder.pdf');
    return api.post('/loads/import-documents', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Brokers
export const brokersApi = {
  getAll: () => api.get('/brokers'),
  create: (data) => api.post('/brokers', data),
  delete: (id) => api.delete(`/brokers/${id}`),
};

// Deductions
export const deductionsApi = {
  getAll: (params) => api.get('/deductions', { params }),
  search: (params) => api.get('/deductions/search', { params }),
  create: (data) => api.post('/deductions', data),
  createBulk: (data) => api.post('/deductions/bulk', data),
  update: (id, data) => api.put(`/deductions/${id}`, data),
  delete: (id) => api.delete(`/deductions/${id}`),
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/deductions/upload-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadFile: (fileId) => api.get(`/deductions/file/${fileId}`, { responseType: 'blob' }),
  deleteFile: (fileId) => api.delete(`/deductions/file/${fileId}`),
};

// Recurring Deductions
export const recurringDeductionsApi = {
  getAll: () => api.get('/recurring-deductions'),
  create: (data) => api.post('/recurring-deductions', data),
  update: (id, data) => api.put(`/recurring-deductions/${id}`, data),
  delete: (id) => api.delete(`/recurring-deductions/${id}`),
  apply: (weekStart) => api.post(`/recurring-deductions/apply/${weekStart}`),
};

// Weekly Summary
export const summaryApi = {
  getForUnit: (unitId, weekStart) => api.get(`/weekly-summary/${unitId}/${weekStart}`),
  getAll: (weekStart) => api.get(`/weekly-summaries/${weekStart}`),
  getRange: (startDate, endDate) =>
    api.get(`/weekly-summaries-range`, { params: { start_date: startDate, end_date: endDate } }),
  exportPdf: (unitId, weekStart, itemized = false) => 
    api.get(`/export/weekly-summary/${unitId}/${weekStart}`, { params: { itemized }, responseType: 'blob' }),
  emailSettlement: (unitId, weekStart, recipientEmail, message) =>
    api.post(`/email-settlement/${unitId}/${weekStart}`, { recipient_email: recipientEmail, message }),
  emailSettlementCustom: (unitId, startDate, endDate, recipientEmail, message, itemized = false) =>
    api.post(`/email-settlement-custom/${unitId}`, { recipient_email: recipientEmail, message }, {
      params: { start_date: startDate, end_date: endDate, itemized },
    }),
  // Email an already-generated PDF blob — guarantees the recipient gets EXACTLY what the user previewed.
  emailSettlementAttachment: (unitId, pdfBlob, filename, periodLabel, netPayout, recipientEmail, message) => {
    const fd = new FormData();
    fd.append('file', pdfBlob, filename || 'Settlement_Pay.pdf');
    if (periodLabel) fd.append('period_label', periodLabel);
    if (netPayout != null) fd.append('net_payout', String(netPayout));
    if (recipientEmail) fd.append('recipient_email', recipientEmail);
    if (message) fd.append('message', message);
    return api.post(`/email-settlement-attachment/${unitId}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  getPreviousBalanceBreakdown: (unitId, weekStart) => 
    api.get(`/previous-balance-breakdown/${unitId}/${weekStart}`),
  generateInvoice: (unitId, weekStart, broker) =>
    api.get(`/generate-invoice/${unitId}/${weekStart}`, { 
      params: broker ? { broker } : {},
      responseType: 'blob' 
    }),
  generateInvoiceCustom: (unitId, startDate, endDate, broker, itemized = false) =>
    api.get(`/generate-invoice-custom/${unitId}`, { 
      params: { start_date: startDate, end_date: endDate, itemized, ...(broker ? { broker } : {}) },
      responseType: 'blob' 
    }),
};

// Settlement Status & Payroll Notes
export const settlementApi = {
  getStatus: (unitId, weekStart) => api.get(`/settlement-status/${unitId}/${weekStart}`),
  getMeta: (weekStart) => api.get(`/settlement-meta/${weekStart}`),
  updateStatus: (data) => api.post('/settlement-status', data),
  getPayrollNotes: (unitId, weekStart) => api.get(`/payroll-notes/${unitId}/${weekStart}`),
  updatePayrollNotes: (data) => api.post('/payroll-notes', data),
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getOverview: () => api.get('/dashboard/overview'),
  getFuel: () => api.get('/dashboard/fuel'),
};

// History
export const historyApi = {
  getWeeks: () => api.get('/history/weeks'),
};

// Users (Admin)
export const usersApi = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  updateRole: (userId, role) => api.put(`/users/${userId}/role?role=${role}`),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Vehicle Management
export const vehicleRecordsApi = {
  getAll: (params) => api.get('/vehicle-records', { params }),
  create: (data) => api.post('/vehicle-records', data),
  update: (id, data) => api.put(`/vehicle-records/${id}`, data),
  delete: (id) => api.delete(`/vehicle-records/${id}`),
  uploadDocument: (formData) => api.post('/vehicle-records/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadDocument: (fileId) => api.get(`/vehicle-documents/${fileId}`, { responseType: 'blob' }),
};

export const vehicleAlertsApi = {
  getAll: (params) => api.get('/vehicle-alerts', { params }),
  create: (data) => api.post('/vehicle-alerts', data),
  update: (id, data) => api.put(`/vehicle-alerts/${id}`, data),
  delete: (id) => api.delete(`/vehicle-alerts/${id}`),
  sendReminders: () => api.post('/send-alert-reminders'),
};

// Driver Files
export const driverFilesApi = {
  getAll: (params) => api.get('/driver-files', { params }),
  create: (data) => api.post('/driver-files', data),
  update: (id, data) => api.put(`/driver-files/${id}`, data),
  delete: (id) => api.delete(`/driver-files/${id}`),
  uploadDocument: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/driver-files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadDocument: (fileId) => api.get(`/driver-files/download/${fileId}`, { responseType: 'blob' }),
  sendReminders: () => api.post('/send-driver-file-reminders'),
};

// Compliance Reports
export const reportsApi = {
  vehicleCompliance: () => api.get('/reports/vehicle-compliance', { responseType: 'blob' }),
  driverCompliance: () => api.get('/reports/driver-compliance', { responseType: 'blob' }),
};

// Company (Admin)
export const companyApi = {
  get: () => api.get('/company'),
  update: (data) => api.put('/company', data),
  getPaystubSettings: () => api.get('/company/paystub-settings'),
  updatePaystubSettings: (data) => api.put('/company/paystub-settings', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/company/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLogo: () => api.delete('/company/logo'),
  migrateDispatchFees: () => api.post('/admin/migrate-dispatch-fees'),
};

// AI Assistant
export const aiApi = {
  chat: (message, sessionId = null) => api.post('/ai/chat', { message, session_id: sessionId }),
  getStatus: () => api.get('/ai/status'),
  extractDriverInfo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/ai/extract-driver-info', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  extractVehicleInfo: (file, unitId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (unitId) {
      formData.append('unit_id', unitId);
    }
    return api.post('/ai/extract-vehicle-info', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  processFuelReport: (file, weekStart) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('week_start', weekStart);
    return api.post('/ai/process-fuel-report', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  applyFuelDeductions: (entries, weekStart) => {
    return api.post('/ai/apply-fuel-deductions', { entries, week_start: weekStart });
  },
};

// Amazon Relay Import
export const amazonRelayApi = {
  process: (tripsFile, paymentFile, weekStart) => {
    const formData = new FormData();
    formData.append('trips_file', tripsFile);
    formData.append('payment_file', paymentFile);
    formData.append('week_start', weekStart);
    return api.post('/amazon-relay/process', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  apply: (entries, weekStart, { forceCrossWeek = false, skipRefs = [] } = {}) => {
    return api.post('/amazon-relay/apply', {
      entries,
      week_start: weekStart,
      force_cross_week: forceCrossWeek,
      skip_load_references: skipRefs,
    });
  },
};


// Driver Applications
export const driverApplicationsApi = {
  // Public - no auth needed
  submit: (data) => api.post('/driver-applications', data),
  // Admin - auth required
  getAll: (status) => api.get('/driver-applications', { params: status ? { status } : {} }),
  getOne: (id) => api.get(`/driver-applications/${id}`),
  updateStatus: (id, status) => api.patch(`/driver-applications/${id}/status`, { status }),
  delete: (id) => api.delete(`/driver-applications/${id}`),
};

// Compliance
export const complianceApi = {
  getDashboardStats: () => api.get('/compliance/dashboard-stats'),
  getDrivers: (params) => api.get('/compliance/drivers', { params }),
  getFleetMap: () => api.get('/compliance/fleet-map'),
};

export const featureApi = {
  getToggles: () => api.get('/company/feature-toggles'),
  updateToggles: (data) => api.put('/company/feature-toggles', data),
};

// Roles & Permissions
export const permissionsApi = {
  getRoles: () => api.get('/permissions/roles'),
  updateRoles: (permissions) => api.put('/permissions/roles', { permissions }),
  resetRoles: () => api.post('/permissions/roles/reset'),
  getMine: () => api.get('/permissions/me'),
};

// Onboarding tour
export const onboardingApi = {
  getStatus: () => api.get('/onboarding/status'),
  complete: (skipped = false) => api.post('/onboarding/complete', { completed: true, skipped }),
};

// Dispatchers
export const dispatchersApi = {
  getAll: (params = {}) => api.get('/dispatchers', { params }),
  get: (id) => api.get(`/dispatchers/${id}`),
  create: (data) => api.post('/dispatchers', data),
  update: (id, data) => api.put(`/dispatchers/${id}`, data),
  delete: (id) => api.delete(`/dispatchers/${id}`),
  performance: (id, params) => api.get(`/dispatchers/${id}/performance`, { params }),
  performanceAll: (params) => api.get('/dispatchers/performance/all', { params }),
  getPaystub: (id, weekStart, endDate = null) =>
    api.get(`/dispatchers/${id}/paystub`, {
      params: endDate ? { week_start: weekStart, end_date: endDate } : { week_start: weekStart },
      responseType: 'blob',
    }),
};

export const unitApi = {
  getPeople: (unitId) => api.get(`/units/${unitId}/people`),
};

// Trailers
export const trailersApi = {
  getAll: () => api.get('/trailers'),
  create: (data) => api.post('/trailers', data),
  update: (id, data) => api.put(`/trailers/${id}`, data),
  delete: (id) => api.delete(`/trailers/${id}`),
};

// Contact form (also used by Company Settings → Contact tab)
export const contactApi = {
  send: (data) => api.post('/contact', data),
};

// Driver pay calculator (pick loads, fixed / % per load)
export const driverPayCalcApi = {
  get: (driverId, weekStart, weeksBack = 4) =>
    api.get(`/drivers/${driverId}/pay-calc`, { params: { week_start: weekStart, weeks_back: weeksBack } }),
  save: (driverId, data) => api.put(`/drivers/${driverId}/pay-calc`, data),
  reset: (driverId, weekStart) => api.delete(`/drivers/${driverId}/pay-calc`, { params: { week_start: weekStart } }),
};

// Paystub Manual Overrides — works for driver / staff / dispatcher / owner_operator
export const paystubOverridesApi = {
  get: (personType, personId, weekStart) =>
    api.get('/paystub-overrides', { params: { person_type: personType, person_id: personId, week_start: weekStart } }),
  upsert: (data) => api.post('/paystub-overrides', data),
  delete: (personType, personId, weekStart) =>
    api.delete('/paystub-overrides', { params: { person_type: personType, person_id: personId, week_start: weekStart } }),
  list: (personType, personId) =>
    api.get('/paystub-overrides/list', { params: { person_type: personType, person_id: personId } }),
};

// Paystub Emails — works for driver / staff / dispatcher / owner_operator
export const paystubEmailsApi = {
  send: (personType, personId, weekStart, recipientEmail = null, message = '', saveToProfile = false) =>
    api.post(`/email-paystub/${personType}/${personId}/${weekStart}`, {
      recipient_email: recipientEmail,
      message,
      save_to_profile: saveToProfile,
    }),
};

// Bulk Email All Settlements for a given week
export const settlementEmailsApi = {
  emailAll: (weekStart) =>
    api.post(`/email-all-settlements/${weekStart}`),
};

// Settlement Additions (bonuses, refunds, adjustments)
export const additionsApi = {
  list: (params) => api.get('/additions', { params }),
  create: (data) => api.post('/additions', data),
  update: (id, data) => api.put(`/additions/${id}`, data),
  delete: (id) => api.delete(`/additions/${id}`),
};

// Schedule
export const scheduleApi = {
  getEntries: () => api.get('/schedule'),
  getDrivers: (includeInactive = false) => api.get('/schedule/drivers', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/schedule', data),
  update: (id, data) => api.put(`/schedule/${id}`, data),
  delete: (id) => api.delete(`/schedule/${id}`),
  splitWeek: (id, weekStart, weekEnd) => api.post(`/schedule/${id}/split-week`, { week_start: weekStart, week_end: weekEnd }),
  exportPdf: (driverId) => api.get('/schedule/export-pdf', { params: driverId ? { driver_id: driverId } : {}, responseType: 'blob' }),
};

// Accounting
export const accountingApi = {
  getSummary: (params) => api.get('/accounting/summary', { params }),
  getUnitBreakdown: (params) => api.get('/accounting/unit-breakdown', { params }),
  getCategories: () => api.get('/accounting/categories'),
  createCategory: (data) => api.post('/accounting/categories', data),
  uploadStatement: (file, periodStart, periodEnd) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('period_start', periodStart);
    fd.append('period_end', periodEnd);
    return api.post('/accounting/upload-statement', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 });
  },
  getJobStatus: (jobId) => api.get(`/accounting/job/${jobId}`),
  getTransactions: (params) => api.get('/accounting/transactions', { params }),
  updateTransaction: (id, data) => api.put(`/accounting/transactions/${id}`, data),
  clearTransactions: (params) => api.delete('/accounting/transactions', { params }),
  // Custom Settlement engine
  listCustomSettlements: (unitId) => api.get('/accounting/custom-settlements', { params: unitId ? { unit_id: unitId } : {} }),
  getCustomSettlement: (id) => api.get(`/accounting/custom-settlements/${id}`),
  previewCustomSettlement: (payload) => api.post('/accounting/custom-settlements/preview', payload),
  createCustomSettlement: (payload) => api.post('/accounting/custom-settlements', payload),
  patchCustomSettlement: (id, payload) => api.patch(`/accounting/custom-settlements/${id}`, payload),
  deleteCustomSettlement: (id) => api.delete(`/accounting/custom-settlements/${id}`),
  customSettlementPdf: (id) => api.get(`/accounting/custom-settlements/${id}/pdf`, { responseType: 'blob' }),
  // Orphan loads (loads with no unit_id — usually imports without a unit)
  listOrphanLoads: () => api.get('/accounting/orphan-loads'),
  autoBackfillOrphans: () => api.post('/accounting/orphan-loads/backfill-auto', {}),
  backfillOrphansToUnit: (payload) => api.post('/accounting/orphan-loads/backfill-unit', payload),
};

// Bank Reconciliation (Phase 1)
export const bankReconApi = {
  uploadFiles: (files, bankAccount, useAiFallback = false) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    if (bankAccount) fd.append('bank_account', bankAccount);
    if (useAiFallback) fd.append('use_ai_fallback', 'true');
    // Small timeout — the request returns a job_id in <1 s now; the actual
    // parsing runs in the background and is polled via `uploadStatus`.
    return api.post('/accounting/statements/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  uploadStatus: (jobId) => api.get(`/accounting/statements/status/${jobId}`, { silentError: true }),
  commit: (payload) => api.post('/accounting/statements/commit', payload),
  listBatches: () => api.get('/accounting/batches'),
  deleteBatch: (batchId) => api.delete(`/accounting/batches/${batchId}`),
  listTxns: (params) => api.get('/accounting/txns', { params }),
  patchTxn: (id, patch) => api.patch(`/accounting/txns/${id}`, patch),
  bulkTxns: (payload) => api.post('/accounting/txns/bulk', payload),
  categorySummary: (params) => api.get('/accounting/category-summary', { params }),
  listRules: () => api.get('/accounting/rules'),
  createRule: (payload) => api.post('/accounting/rules', payload),
  patchRule: (id, patch) => api.patch(`/accounting/rules/${id}`, patch),
  deleteRule: (id) => api.delete(`/accounting/rules/${id}`),
  applyRule: (id) => api.post(`/accounting/rules/${id}/apply`),
  reapplyAllRules: () => api.post('/accounting/rules/reapply-all'),
  defaultCategories: () => api.get('/accounting/default-categories'),
  listCategories: () => api.get('/accounting/txn-categories'),
  createCategory: (payload) => api.post('/accounting/txn-categories', payload),
  patchCategory: (id, patch) => api.patch(`/accounting/txn-categories/${id}`, patch),
  reorderCategories: (order) => api.post('/accounting/txn-categories/reorder', { order }),
  deleteCategory: (id, migrateTo) => api.delete(`/accounting/txn-categories/${id}`, { params: { migrate_to: migrateTo || 'uncategorized' } }),
  // Subcategories
  listSubcategories: (categoryId) => api.get('/accounting/subcategories', { params: categoryId ? { category_id: categoryId } : {} }),
  createSubcategory: (payload) => api.post('/accounting/subcategories', payload),
  patchSubcategory: (id, patch) => api.patch(`/accounting/subcategories/${id}`, patch),
  deleteSubcategory: (id) => api.delete(`/accounting/subcategories/${id}`),
  convertToSubcategory: (catId, targetCategoryId) => api.post(`/accounting/txn-categories/${catId}/convert-to-subcategory`, { target_category_id: targetCategoryId }),
  // Rule autocomplete
  previewRuleMatches: (params) => api.get('/accounting/rules/preview-matches', { params }),
  facets: () => api.get('/accounting/facets'),
  statementPdf: (params) => api.get('/accounting/statement-pdf', { params, responseType: 'blob' }),
  runDeductionCheck: (payload) => api.post('/accounting/txns/run-deduction-check', payload || {}),
  createManualTxn: (payload) => api.post('/accounting/txns/manual', payload),
};

// ────────────────────────────────────────────────────────────────────────────
// Activity log — admin-only feed + compact bank-recon panel
// ────────────────────────────────────────────────────────────────────────────
export const activityLogApi = {
  list: (params) => api.get('/activity-log', { params }),
  bankRecon: (params) => api.get('/activity-log/bank-recon', { params }),
};

// ────────────────────────────────────────────────────────────────────────────
// Settlement schedules — auto-email settlements on a cadence
// ────────────────────────────────────────────────────────────────────────────
export const settlementSchedulesApi = {
  list: () => api.get('/settlement-schedules'),
  unitRecipients: () => api.get('/settlement-schedules/unit-recipients'),
  create: (payload) => api.post('/settlement-schedules', payload),
  patch: (id, payload) => api.patch(`/settlement-schedules/${id}`, payload),
  delete: (id) => api.delete(`/settlement-schedules/${id}`),
  runNow: (id) => api.post(`/settlement-schedules/${id}/run-now`),
};

export default api;
